import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { clearAuth, useAuth } from '@/features/auth/authStore';
import { switchOptions as reportSwitchOptions, type DashboardConfig } from '@/features/dashboard/config';
import { formatRelativeNL } from '@/features/ah/utils';
import { usePrijsfavorietenReport } from '@/features/prijsfavorieten/usePrijsfavorietenReport';
import { calendarWeekSpan, PF_SECTIONS } from '@/features/prijsfavorieten/utils';
import { TopBar } from '@/features/prijsfavorieten/components/TopBar';
import { SubNav } from '@/features/prijsfavorieten/components/SubNav';
import { Hero } from '@/features/prijsfavorieten/components/Hero';
import { PeriodStrip } from '@/features/prijsfavorieten/components/PeriodStrip';
import { KPIGrid, PartTotalsHead } from '@/features/prijsfavorieten/components/KPIGrid';
import { WeeklyTable } from '@/features/prijsfavorieten/components/WeeklyTable';
import { EngagementSection } from '@/features/prijsfavorieten/components/EngagementSection';
import { GameplaySection } from '@/features/prijsfavorieten/components/GameplaySection';
import { BehaviourSection } from '@/features/prijsfavorieten/components/BehaviourSection';
import { PartsSection } from '@/features/prijsfavorieten/components/PartsSection';
import { CompareEngagementSection } from '@/features/prijsfavorieten/components/CompareEngagementSection';
import { CompareGameplaySection } from '@/features/prijsfavorieten/components/CompareGameplaySection';
import { CompareBehaviourSection } from '@/features/prijsfavorieten/components/CompareBehaviourSection';
import '@/features/ah/ah.css';
import '@/features/prijsfavorieten/prijsfavorieten.css';

/*
 * AH Prijsfavorieten dashboard — thin orchestrator: fetch (with fixture
 * fallback until the pipeline lands), scroll-spy for the sub-nav, and the four
 * sections from the design in order.
 *
 * Two layouts on one payload contract: a payload that carries `previous` (Deel 1)
 * gets the Deel 1 vs Deel 2 design; one without it — every payload from before
 * Deel 2 — keeps the single-part layout it always had.
 */
export function PrijsfavorietenDashboardPage({ config }: { config: DashboardConfig }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isPreview, lastRefreshedAt } = usePrijsfavorietenReport(config.reportId, config.apiBaseUrl);
  // Deel 1, frozen, embedded by the pipeline from Deel 2 on (see report.ts).
  const previous = data.previous;

  const [activeSection, setActiveSection] = useState('overzicht');

  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);
  const updatedLabel = formatRelativeNL(lastRefreshedAt, nowMs) ?? '—';

  useEffect(() => {
    const onScroll = () => {
      const above = PF_SECTIONS.map((s) => {
        const el = document.getElementById(s.id);
        return { id: s.id, top: el ? el.getBoundingClientRect().top : Infinity };
      }).filter((p) => p.top < 200);
      if (above.length > 0) setActiveSection(above[above.length - 1].id);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const switchOptions = reportSwitchOptions(auth?.allowedReports ?? [], auth?.role === 'admin', config.reportId);

  function onSignOut() {
    clearAuth();
    queryClient.clear();
    navigate('/login', { replace: true });
  }

  return (
    <div className="ah-root">
      <div className="app">
        <TopBar
          campaign={data.campaign}
          previous={previous?.campaign}
          updatedLabel={updatedLabel}
          isPreview={isPreview}
          onSwitch={(p) => navigate(p)}
          switchOptions={switchOptions}
          onSignOut={onSignOut}
          isAdmin={auth?.role === 'admin'}
          onManageUsers={() => navigate('/admin/users')}
        />
        <SubNav active={activeSection} setActive={setActiveSection} />
        <main className="main">
          {previous ? (
            // Two parts (from Deel 2 on): the running part's figures, each
            // section comparing it with the frozen Deel 1.
            <>
              <section id="overzicht">
                <Hero campaign={data.campaign} previous={previous.campaign} />
                <PartTotalsHead campaign={data.campaign} />
                <KPIGrid data={data} scope={data.campaign.periodLabel} />
                <PartsSection previous={previous} current={data} />
              </section>
              <CompareEngagementSection previous={previous} current={data} />
              <CompareGameplaySection previous={previous} current={data} />
              <CompareBehaviourSection previous={previous} current={data} />
              <div className="footer">
                {data.campaign.name} · {previous.campaign.periodLabel}: {calendarWeekSpan(previous.campaign.calendarWeeks)} · {data.campaign.periodLabel}:{' '}
                {calendarWeekSpan(data.campaign.calendarWeeks)} · Data wordt elke 30–60 minuten ververst
              </div>
            </>
          ) : (
            <>
              <section id="overzicht">
                <Hero campaign={data.campaign} />
                <PeriodStrip campaign={data.campaign} weeks={data.weeks} />
                <KPIGrid data={data} />
                <WeeklyTable weekly={data.weekly} />
              </section>
              <EngagementSection data={data} />
              <GameplaySection gameplay={data.gameplay} />
              <BehaviourSection behaviour={data.behaviour} />
              <div className="footer">
                {data.campaign.name} · {data.campaign.start} – {data.campaign.end} · Data wordt elke 30–60 minuten ververst
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
